import React, { useState } from 'react';
import { Cloud, X } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Banner promoting Google account sync
 * Shows below WeeklyBanner for users who are not logged in
 */
export default function GoogleSyncBanner() {
  const { t } = useI18n();
  const { user, login, loading } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Storage key and dismissal duration (7 days)
  const dismissedKey = 'markerSync_googleBannerDismissed';
  const DISMISSAL_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  // Check if banner was dismissed recently
  const isDismissedRecently = () => {
    if (typeof window === 'undefined') return false;

    const dismissedAt = localStorage.getItem(dismissedKey);
    if (!dismissedAt) return false;

    const dismissedTime = parseInt(dismissedAt, 10);
    const now = Date.now();

    // Check if dismissal has expired
    if (now - dismissedTime > DISMISSAL_DURATION) {
      localStorage.removeItem(dismissedKey);
      return false;
    }

    return true;
  };

  // Don't show if user is logged in, loading, dismissed in state, or dismissed recently
  if (loading || user || isDismissed || isDismissedRecently()) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem(dismissedKey, Date.now().toString());
    }
  };

  const handleSignIn = async () => {
    setIsLoggingIn(true);
    await login();
    setIsLoggingIn(false);
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b border-blue-100">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Content */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-1.5 bg-blue-100 rounded-lg shrink-0">
              <Cloud size={18} className="text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">
                {t('googleSyncBanner.title')}
              </p>
              <p className="text-xs text-slate-600 hidden sm:block">
                {t('googleSyncBanner.description')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleSignIn}
              disabled={isLoggingIn}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoggingIn ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              <span className="hidden sm:inline">{t('googleSyncBanner.cta')}</span>
              <span className="sm:hidden">{t('auth.signIn')}</span>
            </button>

            <button
              onClick={handleDismiss}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              title={t('googleSyncBanner.dismiss')}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
